import { useEffect, useRef, useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Clapperboard,
  Layers3,
  Loader2,
  Mail,
  ShieldCheck,
} from "lucide-react";
import { ApiError, post } from "../lib/api";
import { useAuth } from "../lib/auth";
import type { AuthUser } from "../lib/auth";
import { Button, Logo } from "./UI";
import "./Login.css";

type Challenge = {
  challenge_id: string;
  email: string;
  expires_in: number;
  resend_after: number;
};
export function Login() {
  const auth = useAuth()!;
  const direct = auth.loginMode === "email";
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [challenge, setChallenge] = useState<Challenge>();
  const [busy, setBusy] = useState<"send" | "verify" | "login" | "">("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [retryAt, setRetryAt] = useState(0);
  const [expiresAt, setExpiresAt] = useState(0);
  const [now, setNow] = useState(Date.now());
  const codeInput = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (direct) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [direct]);
  useEffect(() => {
    if (challenge) codeInput.current?.focus();
  }, [challenge]);
  const remaining = Math.max(0, Math.ceil((retryAt - now) / 1000));
  const expired = !!challenge && now >= expiresAt;
  async function login() {
    if (busy || !email.trim()) return;
    setBusy("login");
    setError("");
    try {
      const result = await post<{ user: AuthUser }>("/auth/login", {
        email: email.trim(),
      });
      auth.signedIn(result.user);
    } catch (failure) {
      setError(
        failure instanceof Error ? failure.message : "登录失败，请重试。",
      );
    } finally {
      setBusy("");
    }
  }
  async function send() {
    if (busy || remaining || !auth.emailConfigured) return;
    setBusy("send");
    setError("");
    setMessage("");
    try {
      const sent = await post<Challenge>("/auth/email-code", {
        email: challenge?.email || email.trim(),
      });
      setChallenge(sent);
      setEmail(sent.email);
      setCode("");
      setRetryAt(Date.now() + sent.resend_after * 1000);
      setExpiresAt(Date.now() + sent.expires_in * 1000);
      setMessage("验证码已发送，请查看收件箱或垃圾邮件。");
      setNow(Date.now());
    } catch (failure) {
      setError(
        failure instanceof Error ? failure.message : "发送失败，请重试。",
      );
      if (
        failure instanceof ApiError &&
        Number(failure.diagnostics.retry_after) > 0
      )
        setRetryAt(Date.now() + Number(failure.diagnostics.retry_after) * 1000);
    } finally {
      setBusy("");
    }
  }
  async function verify() {
    if (!challenge || busy || code.length !== 6 || expired) return;
    setBusy("verify");
    setError("");
    try {
      const result = await post<{ user: AuthUser }>("/auth/verify", {
        challenge_id: challenge.challenge_id,
        code,
      });
      auth.signedIn(result.user);
    } catch (failure) {
      setError(
        failure instanceof Error ? failure.message : "登录失败，请重试。",
      );
      if (
        failure instanceof ApiError &&
        ["CODE_EXPIRED", "CODE_ATTEMPTS_EXCEEDED"].includes(failure.code)
      )
        setExpiresAt(0);
      codeInput.current?.select();
    } finally {
      setBusy("");
    }
  }
  return (
    <div className="login-page">
      <header className="login-header">
        <Logo />
        <a className="login-home" href="#/">
          <ArrowLeft size={14} /> 返回首页
        </a>
      </header>
      <main className="login-main">
        <section className="login-intro" aria-label="欢迎使用 AClip">
          <span className="login-eyebrow">
            <span /> 从一个好想法开始
          </span>
          <h1>
            让创作，
            <br />
            <span>自然发生。</span>
          </h1>
          <p>
            整理脚本、设计分镜、生成预览。
            <br />
            在一个工作台里，把想法变成作品。
          </p>
          <div className="login-illustration" aria-hidden="true">
            <div className="login-mini-window">
              <div className="login-dots">
                <i />
                <i />
                <i />
              </div>
              <span>AClip Studio</span>
              <Clapperboard size={16} />
            </div>
            <div className="login-canvas">
              <Layers3 size={46} strokeWidth={1.2} />
              <span>每一镜，都有自己的表达。</span>
            </div>
            <div className="login-timeline">
              <i />
              <i />
              <i />
              <i />
            </div>
            <span className="login-done">
              <Check size={14} /> 想法，正在成片
            </span>
          </div>
        </section>
        <section className="login-card" aria-labelledby="login-title">
          <div className="login-mail-icon">
            <Mail size={25} strokeWidth={1.5} />
          </div>
          <h2 id="login-title">
            {challenge ? "查看你的邮箱" : "登录你的工作台"}
          </h2>
          <p className="login-description">
            {challenge ? (
              <>
                验证码已发送至 <strong>{challenge.email}</strong>
              </>
            ) : direct ? (
              "输入邮箱即可登录，首次登录自动创建账号。"
            ) : (
              "使用邮箱验证码登录，首次验证后自动创建账号。"
            )}
          </p>
          {auth.error && (
            <div className="login-feedback error" role="alert">
              {auth.error}
              <button onClick={() => void auth.refresh()}>重新连接</button>
            </div>
          )}
          {!direct && !auth.emailConfigured && !auth.error && (
            <div className="login-feedback setup" role="status">
              <b>邮件登录尚未配置</b>
              <span>
                请联系管理员设置 Resend API Key
                和已验证的发件邮箱，配置完成后即可登录。
              </span>
              <button onClick={() => void auth.refresh()}>刷新配置状态</button>
            </div>
          )}
          <form
            onSubmit={(event) => {
              event.preventDefault();
              void (direct ? login() : challenge ? verify() : send());
            }}
          >
            {challenge ? (
              <>
                <label htmlFor="login-code">6 位验证码</label>
                <input
                  ref={codeInput}
                  id="login-code"
                  className="login-code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  required
                  value={code}
                  onChange={(event) => {
                    setCode(event.target.value.replace(/\D/g, "").slice(0, 6));
                    setError("");
                  }}
                  aria-describedby="login-code-hint"
                  aria-invalid={!!error}
                  disabled={!!busy}
                />
                <p
                  id="login-code-hint"
                  className={`login-hint ${expired ? "is-expired" : ""}`}
                >
                  {expired
                    ? "验证码已失效，请重新发送。"
                    : "验证码 10 分钟内有效，仅可使用一次。"}
                </p>
              </>
            ) : (
              <>
                <label htmlFor="login-email">邮箱地址</label>
                <input
                  id="login-email"
                  type="email"
                  name="email"
                  autoComplete="email"
                  autoFocus
                  placeholder="name@example.com"
                  required
                  maxLength={254}
                  value={email}
                  onChange={(event) => {
                    setEmail(event.target.value);
                    setError("");
                  }}
                  disabled={!!busy}
                  aria-invalid={!!error}
                />
              </>
            )}
            {error && (
              <div className="login-feedback error" role="alert">
                {error}
              </div>
            )}
            {message && !error && (
              <div className="login-feedback success" role="status">
                {message}
              </div>
            )}
            <Button
              className="login-submit"
              variant="primary"
              type="submit"
              loading={!!busy}
              disabled={
                !!busy ||
                (!direct && !auth.emailConfigured) ||
                (direct
                  ? !email.trim()
                  : challenge
                    ? code.length !== 6 || expired
                    : !email.trim() || remaining > 0)
              }
            >
              {direct
                ? busy
                  ? "正在登录…"
                  : "登录并进入工作台"
                : challenge
                  ? "验证并登录"
                  : remaining
                    ? `${remaining} 秒后可重新发送`
                    : "获取验证码"}
              <ArrowRight size={17} />
            </Button>
          </form>
          {challenge && (
            <div className="login-code-actions">
              <button
                type="button"
                disabled={!!busy}
                onClick={() => {
                  setChallenge(undefined);
                  setCode("");
                  setError("");
                  setMessage("");
                }}
              >
                <ArrowLeft size={14} /> 更换邮箱
              </button>
              <button
                type="button"
                disabled={!!busy || remaining > 0}
                onClick={() => void send()}
              >
                {busy === "send" ? (
                  <>
                    <Loader2 className="spin" size={14} /> 正在发送
                  </>
                ) : remaining ? (
                  `${remaining} 秒后重发`
                ) : (
                  "重新发送验证码"
                )}
              </button>
            </div>
          )}
          <div className="login-security">
            {direct ? <Mail size={15} /> : <ShieldCheck size={15} />}
            <span>
              {direct
                ? "使用同一邮箱，可继续查看你的项目、素材和任务。"
                : "无需密码，验证成功后即可进入工作台。"}
            </span>
          </div>
        </section>
      </main>
      <footer className="login-footer">AClip Studio · 专注每一镜的创作</footer>
    </div>
  );
}

export function LoginLoading() {
  return (
    <div className="login-loading" role="status">
      <Logo />
      <Loader2 size={22} className="spin" />
      <p>正在确认登录状态…</p>
    </div>
  );
}
